import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { likePost, addComment, deletePost } from "../../services/api";
import CommentForm from "./CommentForm";
import CommentList from "./CommentList";
import PostEditModal from "./PostEditModal";
import { useAuth } from "../../auth/AuthContext";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, Heart, MessageCircle } from "lucide-react";

const PostItem = ({ post, onPostUpdate, onPostDelete }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [likes, setLikes] = useState(post.likes_count || 0);
  const [liked, setLiked] = useState(post.liked_by_user || false);
  const [comments, setComments] = useState(post.comments || []);
  const [showComments, setShowComments] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [visible, setVisible] = useState(false);

  const isAuthor = post?.is_author;
  const profileLink = isAuthor ? "/profile" : `/perfil/${post.author?.id}`;

  // 🎌 Diccionario de banderas por país
  const countryFlags = {
    AT: "🇦🇹", BE: "🇧🇪", BG: "🇧🇬", HR: "🇭🇷", CY: "🇨🇾", CZ: "🇨🇿",
    DK: "🇩🇰", EE: "🇪🇪", FI: "🇫🇮", FR: "🇫🇷", DE: "🇩🇪", GR: "🇬🇷",
    HU: "🇭🇺", IE: "🇮🇪", IT: "🇮🇹", LV: "🇱🇻", LT: "🇱🇹", LU: "🇱🇺",
    MT: "🇲🇹", NL: "🇳🇱", PL: "🇵🇱", PT: "🇵🇹", RO: "🇷🇴", SK: "🇸🇰",
    SI: "🇸🇮", ES: "🇪🇸", SE: "🇸🇪"
  };

  const authorFlag =
    post.author?.country && countryFlags[post.author.country.toUpperCase()]
      ? countryFlags[post.author.country.toUpperCase()]
      : "";

  // 🎬 Animación de entrada
  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timeout);
  }, []);

  // 🔄 Mantener sincronizados likes/comentarios si `post` cambia externamente
  useEffect(() => {
    setLikes(post.likes_count || 0);
    setLiked(post.liked_by_user || false);
    setComments(post.comments || []);
  }, [post]);

  const handleLike = async () => {
    try {
      const res = await likePost(post.id);
      const { liked: wasLiked, total_likes } = res.data;

      setLiked(wasLiked);
      setLikes(total_likes);

      // 🔄 Notificar al padre
      onPostUpdate({
        ...post,
        likes_count: total_likes,
        liked_by_user: wasLiked,
      });
    } catch (err) {
      console.error("❌ Error al dar like", err);
    }
  };

  const handleNewComment = async (text) => {
    try {
      const res = await addComment(post.id, text);
      const newComment = res.data;
      setComments((prev) => [...prev, newComment]);

      onPostUpdate({
        ...post,
        comments: [...comments, newComment],
      });
    } catch (err) {
      console.error("❌ Error al comentar", err);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deletePost(post.id);
      onPostDelete(post.id);
    } catch (err) {
      console.error("❌ Error al eliminar post", err);
      alert(t("post.delete_error", "No se pudo eliminar el post."));
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <div
        className={`transition-all duration-300 transform ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        } bg-white border border-[#E5EDF3] rounded-[24px] p-5 sm:p-6 shadow-sm hover:shadow-md`}
      >
        {/* Autor */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
          <Link to={profileLink}>
            <img
              src={post.author?.photo || "/default-avatar.png"}
              alt={t("post.item.author_photo_alt")}
              className="w-12 h-12 rounded-full object-cover border-2 border-sky-100 shadow-sm"
            />
          </Link>
          <div>
            <Link
              to={profileLink}
              className="text-[#245580] hover:underline font-bold flex items-center gap-1 text-base font-semibold"
            >
              {post.author?.nombre || t("post.item.user_placeholder")}
              {authorFlag && <span className="ml-1">{authorFlag}</span>}
            </Link>
            <span className="text-slate-400 block mt-1 text-xs">
              {new Date(post.created_at).toLocaleString()}
            </span>
          </div>
        
          </div>

          {isAuthor && !showConfirm && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center justify-center w-10 h-10 bg-sky-50 hover:bg-sky-100 text-[#247BA8] rounded-xl transition" aria-label={t("post.item.edit")} title={t("post.item.edit")}
              >
                <Pencil size={18} strokeWidth={2} />
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                className="inline-flex items-center justify-center w-10 h-10 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition" aria-label={t("post.item.delete")} title={t("post.item.delete")}
              >
                <Trash2 size={18} strokeWidth={2} />
              </button>
            
            </div>
          )}
</div>

        {/* Imagen */}
        {post.image_url && (
          <img
            src={post.image_url}
            alt={t("post.item.image_alt")}
            onClick={() => setModalImage(post.image_url)}
            className="mb-5 max-h-[520px] w-full cursor-zoom-in rounded-2xl bg-slate-50 object-contain transition hover:brightness-95"
          />
        )}

        {/* Texto */}
        <p className="mb-5 whitespace-pre-wrap break-words text-base leading-7 text-slate-700 sm:text-lg">{post.text}</p>

        {/* Botones */}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
          <button
            onClick={handleLike}
            className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
              liked
                ? "border-rose-200 bg-rose-50 text-rose-600"
                : "border-[#D8E7F0] bg-white text-slate-600 hover:bg-[#F2F8FC]"
            }`}
          >
            <Heart
              size={19}
              strokeWidth={2}
              fill={liked ? "currentColor" : "none"}
            />
            <span>{likes}</span>
          </button>

          <button
            onClick={() => setShowComments((prev) => !prev)}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#D8E7F0] bg-white px-4 py-2 text-sm font-medium text-[#245580] transition hover:bg-[#F2F8FC]"
          >
            <MessageCircle size={19} strokeWidth={2} />
            <span>{comments.length}</span>
          </button>

          
        </div>



        {/* HAPPYCAP_FEED_HARMONY_V1 */}
        <style>{`
          .feed-comments-modern form {
            gap: 10px !important;
            align-items: stretch !important;
          }

          .feed-comments-modern form input[type="text"] {
            border: 1px solid #d8e7f0 !important;
            border-radius: 14px !important;
            background: white !important;
            padding: 12px 14px !important;
            font-size: 14px !important;
            outline: none !important;
          }

          .feed-comments-modern form input[type="text"]:focus {
            border-color: #247ba8 !important;
            box-shadow: 0 0 0 2px rgba(36, 123, 168, 0.12);
          }

          .feed-comments-modern form button[type="submit"] {
            align-self: flex-end !important;
            border-radius: 999px !important;
            background: #247ba8 !important;
            padding: 9px 20px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
          }

          .feed-comments-modern form button[type="submit"]:hover:not(:disabled) {
            background: #19658e !important;
          }

          .feed-comments-modern form button[type="submit"]:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>

        {/* Comentarios */}
        {showComments && (
          <div className="feed-comments-modern mt-5 rounded-2xl bg-[#F5F9FC] p-4 sm:p-5">
            <CommentForm onSubmit={handleNewComment} />
            <CommentList
              comments={comments}
              onCommentUpdated={(updated) => {
                const nextComments = comments.map((comment) =>
                  comment.id === updated.id ? updated : comment
                );
                setComments(nextComments);
                onPostUpdate({ ...post, comments: nextComments });
              }}
              onCommentDeleted={(deletedId) => {
                const nextComments = comments.filter(
                  (comment) => comment.id !== deletedId
                );
                setComments(nextComments);
                onPostUpdate({
                  ...post,
                  comments: nextComments,
                  comments_count: nextComments.length,
                });
              }}
            />
          </div>
        )}

        {/* Modal de edición */}
        {showEditModal && (
          <PostEditModal
            post={post}
            onClose={() => setShowEditModal(false)}
            onUpdated={onPostUpdate}
            onDeleted={onPostDelete}
          />
        )}
      </div>

      {/* Confirmación de borrado centrada en la pantalla */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60 px-4 py-6"
          onClick={() => {
            if (!deleting) setShowConfirm(false);
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={`delete-post-title-${post.id}`}
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id={`delete-post-title-${post.id}`}
              className="mb-5 text-lg font-semibold leading-relaxed text-slate-800"
            >
              {t("post.item.confirm_delete")}
            </h3>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl bg-red-600 px-5 py-3 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting
                  ? t("post.edit_modal.deleting")
                  : t("post.edit_modal.confirm_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de imagen */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-start justify-center z-50 pt-20"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative bg-white p-4 rounded-lg shadow-lg max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-3 right-4 text-gray-600 hover:text-red-500 text-lg"
            >
              ✖
            </button>
            <img
              src={modalImage}
              alt={t("post.list.expanded_image_alt")}
              className="w-full max-h-[80vh] object-contain rounded-md"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PostItem;
